import React from "react";
import { useTranslation } from "react-i18next";
import {
  IconBan,
  IconBell,
  IconBellPlus,
  IconChevronRight,
  IconEdit,
  IconLeaf,
  IconPillFilled,
  IconShoppingCart,
  IconShoppingCartOff,
  IconShoppingCartPlus,
} from "@tabler/icons-react";
import MOptionsMenu from "@/components/ui/atoms/MOptionsMenu";
import { MedicineItemType } from "@/types/pillbox";
import { Reminders } from "@/types/reminders";
import { getTertiaryText } from "@/pages/reminders/remindersHelper";
import styles from "./MedicineCard.module.css";

export interface MedicineCardProps {
  itemType: MedicineItemType;
  title: string;
  frequencyLine?: string;
  trailingIcon?: "chevron" | "dots";
  onClick?: () => void;
  onEditDetails?: () => void;
  /** Reminder already set for this medicine, if any — shows its schedule
   *  inline (same formatting as the Reminders listing screen) and swaps
   *  the options-menu action from "Add reminder" to "Edit reminder". */
  reminder?: Reminders | null;
  onAddReminder?: () => void;
  onEditReminder?: () => void;
  /** Whether this medicine is currently flagged for the shopping list —
   *  adding a medicine to Pillbox no longer adds it automatically. Still
   *  drives the options-menu "Add/Remove from shopping list" label even
   *  when showRefillBadge is false. */
  needsRefill?: boolean;
  onToggleRefill?: () => void;
  onStopMedication?: () => void;
  /** The cart badge only shows in List view — Schedule view passes
   *  needsRefill (for the options-menu label) but suppresses the badge. */
  showRefillBadge?: boolean;
}

const MedicineCard: React.FC<MedicineCardProps> = ({
  itemType,
  title,
  frequencyLine,
  trailingIcon = "chevron",
  onClick,
  onEditDetails,
  reminder,
  onAddReminder,
  onEditReminder,
  needsRefill,
  onToggleRefill,
  onStopMedication,
  showRefillBadge = true,
}) => {
  const { t } = useTranslation();
  const isSupplement = itemType === MedicineItemType.SUPPLEMENT;

  const content = (
    <>
      <span
        className={styles.iconBox}
        style={{
          background: isSupplement
            ? "linear-gradient(92deg, #e7f5ef 0.4%, #edf3fc 99.35%)"
            : "linear-gradient(92deg, #fef3e6 0.4%, #fde5eb 99.35%)",
        }}
      >
        {isSupplement ? (
          <IconLeaf size={24} stroke={1.5} color="#45168C" />
        ) : (
          <IconPillFilled size={24} color="#45168C" />
        )}
        {needsRefill && showRefillBadge && (
          <span className={styles.cartBadge}>
            <IconShoppingCart size={12} stroke={2} color="#fff" />
          </span>
        )}
      </span>
      <span className={styles.content}>
        <span className={styles.titleRow}>
          <span className={styles.title}>{title}</span>
          <IconChevronRight size={12} className={styles.titleChevron} />
        </span>
        {frequencyLine && (
          <span className={styles.frequencyLine}>{frequencyLine}</span>
        )}
        {reminder && (
          <span className={styles.reminderTimes}>
            <IconBell size={12} />
            {getTertiaryText(reminder)}
          </span>
        )}
      </span>
    </>
  );

  if (trailingIcon === "dots") {
    return (
      <div className={styles.card}>
        <button
          type="button"
          className={styles.cardClickable}
          onClick={onClick}
        >
          {content}
        </button>
        <MOptionsMenu
          options={[
            {
              id: "edit",
              label: t("pillbox.optionsMenu.editDetails"),
              icon: <IconEdit size={18} />,
              onClick: () => onEditDetails?.(),
            },
            ...(onToggleRefill
              ? [
                  needsRefill
                    ? {
                        id: "remove-from-shopping-list",
                        label: t("pillbox.optionsMenu.removeFromShoppingList"),
                        icon: <IconShoppingCartOff size={18} />,
                        onClick: () => onToggleRefill(),
                      }
                    : {
                        id: "add-to-shopping-list",
                        label: t("pillbox.optionsMenu.addToShoppingList"),
                        icon: <IconShoppingCartPlus size={18} />,
                        onClick: () => onToggleRefill(),
                      },
                ]
              : []),
            ...(reminder && onEditReminder
              ? [
                  {
                    id: "edit-reminder",
                    label: t("pillbox.optionsMenu.editReminder"),
                    icon: <IconBellPlus size={18} />,
                    onClick: () => onEditReminder(),
                  },
                ]
              : !reminder && onAddReminder
              ? [
                  {
                    id: "add-reminder",
                    label: t("pillbox.optionsMenu.addReminder"),
                    icon: <IconBellPlus size={18} />,
                    onClick: () => onAddReminder(),
                  },
                ]
              : []),
            {
              id: "stop",
              label: t("pillbox.optionsMenu.stopMedication"),
              icon: <IconBan size={18} />,
              onClick: () => onStopMedication?.(),
              delete: true,
            },
          ]}
        />
      </div>
    );
  }

  return (
    <button type="button" className={styles.card} onClick={onClick}>
      {content}
      <IconChevronRight size={24} className={styles.trailingChevron} />
    </button>
  );
};

export default MedicineCard;
